import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { changePageTitle } from 'src/domains/global/actions';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Section from './Section';
import Job from './Job';
import Skill from './Skill';


const Main: React.FC = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(changePageTitle('System'));
    });

    return <Box
        component="main"
        sx={{
            display: 'flex',
            minHeight: '100vh',
            width: '100%',
            flexDirection: 'column',
            backgroundColor: '#eee',
            fontFamily: 'Arial',
            fontSize: '10pt'
        }}
    >
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                backgroundColor: '#eee',
                justifyContent: 'space-around',
                justifySelf: 'center'
            }}
        >

            <Box
                sx={{
                    paddingLeft: 4,
                    paddingRight: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    width: 890,
                    backgroundColor: '#fff',
                }}
            >

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >

                    <Section noDivider>
                        <Box
                            sx={{ m: 0 }}
                            id='main-header'
                        >
                            <Box
                                component='h2'
                                sx={{ m: 0 }}
                            >
                                <span style={{}}>Mikalai "Nick" Miatlitski</span>
                            </Box>
                            <Box
                                component='p'
                                sx={{ m: 0 }}
                            >
                                <span>Chanhassen MN, 55317, </span>
                                <span>(+1) 612-242-8696, </span>
                                <span>miatlitski@gmail.com, </span>
                                <span>https://www.restapicms.com</span>
                            </Box>
                        </Box>
                    </Section>

                    <Section title='Summary'>
                        <Box component={'p'} margin={0}>
                            Software Engineer with over 15 years of experience in full-stack development and SaaS architecture.
                            Proficient in Java EE, JavaScript/Typescript and modern frameworks like React, delivering high-performance applications.
                            Achievements include architecting a multi-tenant platform that automates backend service generation, enhancing operational efficiency.
                            Skilled in optimizing API performance and implementing robust security measures.
                            Ready to leverage expertise to drive innovative solutions and improve project outcomes.
                        </Box>
                    </Section>

                    <Section title='Work Experience' noMargin>
                        <Job
                            title='Founder & Lead Engineer'
                            company='RestAPICms'
                            url='www.restapicms.com'
                            start={2020}
                            description='Developer platform that automatically generates backend services (database schema, REST APIs, and admin console) from user-defined configuration. Platform dynamically provisions backend services using Docker containers and reverse-proxy routing.'
                            accomplishments={[
                                'Architected and built a multi-tenant SaaS platform enabling users to create backend services without writing server code.',
                                'Implemented automatic backend generation including database schema, REST endpoints, OpenAPI documentation, and administrative UI.',
                                'Implemented service routing and isolation for dynamically provisioned backend instances using Docker containers and Nginx reverse proxy.',
                                'Developed an advanced query engine allowing complex filtering criteria through UI.',
                                'Built data visualization components using D3.js.',
                                'Implemented role-based access control using Spring Security and JWT.',
                                'Deployed production infrastructure on AWS EC2 with Linux services.'
                            ]}
                            stack={['Java', 'Spring Boot', 'Hibernate', 'Query DSL', 'Maven', 'Node.js', 'Typescript', 'React', 'Redux', 'Saga', 'D3.js', 'MySql', 'Docker', 'AWS', 'Terraform']}
                        />

                        <Job
                            title='Full Stack Developer'
                            company='Morgan Stanley'
                            url='www.morganstanley.com'
                            start={2022}
                            end={2026}
                            accomplishments={[
                                'Improved application security by identifying and remediating vulnerabilities through implementation of secure coding practices.',
                                'Optimized server-side JSON processing, significantly improving API response performance.',
                                'Designed and implemented full-stack investment business solutions.'
                            ]}
                            stack={['Java', 'Spring Boot', 'Hibernate', 'Gradle', 'Git', 'Ext.js', 'PostgreSql', 'AWS']}
                        />

                        <Job
                            title='React Developer'
                            company='PushPay'
                            url='www.churchcommunitybuilder.com'
                            start={2021}
                            end={2022}
                            accomplishments={[
                                'Developed reusable React components and integrated them into the legacy codebase.',
                                'Created end-to-end integration tests for new functionality.',
                                'Maintained a React Native mobile application.'
                            ]}
                            stack={['React.js', 'React Native', 'Redux', 'Expo', 'Selenium']}
                        />

                        <Job
                            title='Front-end Developer'
                            company='SpecterOps'
                            url='www.specterops.io'
                            start={2020}
                            end={2021}
                            accomplishments={[
                                'Developed high-performance reusable React components based on Material-UI.',
                                'Built data-driven UI consuming REST APIs.',
                                'Implemented data visualization using Regraph and Highcharts.',
                                'Created unit tests (React Testing Library) and E2E tests using Cypress.',
                            ]}
                            stack={['React.js', 'React Native', 'Redux', 'Typescript', 'Regraph.js', 'Neo4j', 'Cypress', 'Kubernetes']}
                        />

                        <Job
                            title='Game Developer (Unity)'
                            start={2016}
                            end={2017}
                            accomplishments={[
                                'Designed 3D models.',
                                'Developed game logic using C#.',
                                'Created working game prototype for mobile platform (Android).'
                            ]}
                            stack={['C#', 'Unity 3D', '3D Max', 'Photoshop']}
                        />

                        {/* <div style={{ breakAfter: 'page' }}></div> */}

                        <Job
                            title='Lead Full-stack Developer'
                            company='API Outsourcing (Fraxion)'
                            url='www.fraxion.biz'
                            start={2016}
                            end={2020}
                            accomplishments={[
                                'Developed a React single-page application.',
                                'Integrated React components into a legacy Java / JSP system.',
                                'Implemented SCIM specification as a configurable Java framework.',
                                'Designed a configurable REST interface using Spring Data REST.',
                                'Improved build pipeline to support CI/CD practices.',
                            ]}
                            stack={['Java', 'Spring Boot', 'Hibernate', 'React.js', 'Redux', 'Webpack', 'Selenium', 'MySql', 'Maven', 'Git', 'HTML/CSS', 'JSP', 'AspectJ', 'MySql', 'MDX', 'icCube']}
                        />

                        <Job
                            title='Tech Lead | Consultant'
                            company='Coherent Solutions, Inc.'
                            url='www.coherentsolutions.com'
                            start={2013}
                            end={2016}
                            accomplishments={[
                                'Led development teams, conducted code reviews, and designed enterprise software solutions.',
                                'Developed and maintained a series of technical designs.',
                                'Led technical meetings to discuss and brainstorm solutions.'
                            ]}
                            stack={['Java', 'Spring', 'Hibernate', 'Maven', 'Git', 'Javascript', 'HTML/CSS', 'Jenkins', 'Bugzilla', 'MySql']}
                        />

                        <Job
                            title='Lead Developer | Software Analyst'
                            company='NMDP'
                            url='www.nmdp.org'
                            start={2012}
                            end={2013}
                            accomplishments={[
                                'Maintained AGNIS platform and modernized web-service architecture.',
                                'Integrated a Maven build and management tool into a legacy project to streamline CI/CD.'
                            ]}
                            stack={['Java', 'Spring', 'Hibernate', 'Maven', 'SQL']}
                        />

                        <Job
                            title='Tech Lead | Consultant'
                            company='Coherent Solutions, Inc.'
                            url='www.coherentsolutions.com'
                            start={2011}
                            end={2012}
                            accomplishments={[
                                'Designed and implemented a highly configurable CMS framework.',
                                'Performed analysis of performance issues, proposed solutions, and supervised their implementation.',
                                'Designed, prototyped, and led the implementation of a responsive mobile and desktop UI.',
                                'Created performance analysis reports using BI tools, icCube, and MDX.',
                                'Developed an on-the-fly, persistent JavaBeans classes generator, based on Hibernate mapping files using ASM library.'
                            ]}
                            stack={['Java', 'Spring', 'Hibernate', 'Maven', 'Javascript', 'HTML/CSS', 'JQuery', 'MySql']}
                        />

                        <Job
                            title='Software Engineer / Senior Software Engineer'
                            company='ISSoft'
                            url='www.issoft.by'
                            start={2006}
                            end={2011}
                            last
                            stack={['Java', 'Spring', 'Hibernate', 'Javascript', 'HTML/CSS', 'JQuery', 'MySql']}
                        />
                    </Section>

                    <Section title='Education'>
                        <Box>{'Belarusian State University of Informatics and Radioelectronics,'} <b>{'Master\'s'}</b> {'in Electronics Engineering'} {'(2003)'}</Box>
                    </Section>

                    <Section title='Skills'>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box>
                                <Skill name="Java EE" />
                                <Skill name="HTML / CSS" />
                                <Skill name="Docker / Kubernetes" />
                                <Skill name="REST / OpenAPI" />
                                <Skill name="Spring / Spring Boot" />
                            </Box>
                            <Box>
                                <Skill name="JavaScript / TypeScript" />
                                <Skill name="RDBMS / MySql / PostgreSql" />
                                <Skill name="AWS" />
                                <Skill name="SaaS / BaaS" />
                                <Skill name="Node.js" />
                            </Box>
                            <Box>
                                <Skill name="React / React Native" />
                                <Skill name="Git" />
                                <Skill name="Maven" />
                                <Skill name="Jenkins" />
                                <Skill name="D3.js" />
                            </Box>
                            <Box>
                                <Skill name="C#" />
                                <Skill name="3D Max" />
                                <Skill name="AutoCAD" />
                                <Skill name="Unity" />
                                <Skill name="Linux" />
                            </Box>
                        </Box>
                    </Section>

                    <Section title='Languages'>
                        <Grid container spacing={5}>
                            <Grid size={4}>
                                <Skill name="English" />
                            </Grid>
                            <Grid size={5}>
                                <Skill name="Russian" />
                            </Grid>
                            <Grid size={3}>
                                <Skill name="Ukranian" />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section title='Certifications'>
                        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                            <Grid size={6}>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}><b>Sun Certified Java Developer</b> (2010)</Box>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}>{'Sun Microsystems'}</Box>
                            </Grid>
                            <Grid size={6}>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}><b>Investment Foundations® Certificate</b> (2024)</Box>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}>{'CFA Institute, Credential ID 93284809'}</Box>
                            </Grid>
                        </Grid>
                    </Section>

                    <Section title='References'>
                        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                            <Grid size={6}>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}><b>Rob Duff</b></Box>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}><b>COO, Coherent Solutions Inc.</b></Box>
                            </Grid>
                            <Grid size={6}>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}>robertd@coherentsolutions.com</Box>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}>(+1) 612-279-6263</Box>
                            </Grid>
                            <Grid size={6}>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}><b>Joe Mullen</b></Box>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}><b>Senior Development Manager, API Outsourcing</b></Box>
                            </Grid>
                            <Grid size={6}>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}>joe.mullen@caribouclan.us</Box>
                                <Box component={'p'} sx={{ p: 0, m: 0 }}>(+1) 651-675-2600</Box>
                            </Grid>
                        </Grid>
                    </Section>

                </Box>
            </Box>
        </Box>
    </Box>
}

export default Main;